import type { Route } from './+types/services.maintenance';
import { Link } from 'react-router';
import { Breadcrumbs, breadcrumbSchemaItems } from '~/components/breadcrumbs';
import { RelatedPages } from '~/components/related-pages';
import { buildMeta, schemaFor, breadcrumbSchema } from '~/lib/seo';
import { SITE, PRICING } from '~/data/site';
import { useBranding } from '~/lib/branding';

export const meta: Route.MetaFunction = () => [
  ...buildMeta({
    title: 'Maintenance Deep Clean Service | Tampa Bay',
    description:
      `One-time maintenance deep cleaning in Tampa Bay. Thorough refresh of your entire home. Per-square-foot pricing starts at ${PRICING.maintenanceRate.toFixed(2)}/sqft. ${PRICING.minimum} minimum. Schedule today.`,
    path: '/maintenance',
    jsonLd: [
      schemaFor('/maintenance'),
      breadcrumbSchema(breadcrumbSchemaItems('/maintenance')),
    ].filter((s): s is Record<string, unknown> => !!s),
  }),
];

export default function MaintenanceServicePage() {
  const branding = useBranding();
  const examplePrice = 2400 * PRICING.maintenanceRate;

  return (
    <main id="main-content">
      <Breadcrumbs path="/maintenance" pageLabel="Maintenance Clean" />
      {/* ── HEADER ───────────────────────────────────────── */}
      <section className="section">
        <div className="container-narrow">
          <p className="eyebrow page-eyebrow">One-Time Service</p>
          <h1 className="display-lg page-h1-full">
            A maintenance deep clean,
            <em className="italic-display">when you need to reset.</em>
          </h1>
          <p className="lede chapter-lede">
            A one-time deep clean for homes that need a refresh but don&rsquo;t require the
            full top-to-bottom reset. This is your answer if your home is regularly maintained
            but certain areas need attention.
          </p>
        </div>
      </section>

      {/* ── PRICING ───────────────────────────────────────── */}
      <section className="section-sm bg-bone-warm">
        <div className="container-narrow">
          <h2 className="display-md">How much does it cost?</h2>
          <div className="price-example price-example-mt reveal">
            <h3 className="display-sm text-bone">${PRICING.maintenanceRate.toFixed(2)} per square foot</h3>
            <div className="price-ex-line price-ex-block-mt">
              <span>Example: 2,400 sqft home<br /><small className="text-mute">Your actual price depends on your home&rsquo;s size.</small></span>
              <span className="price-ex-total price-ex-total-sm">${Math.round(examplePrice)}</span>
            </div>
            <div className="price-ex-line">
              <span>Minimum on every service<br /><small className="text-mute">Even your smallest clean is professional work.</small></span>
              <span className="price-ex-total price-ex-total-sm">${PRICING.minimum}</span>
            </div>
            <p className="marginalia price-example-marginalia">
              See your exact price for your home in under 60 seconds.
            </p>
          </div>
          <div className="section-cta-left">
            <Link to="/quote" className="btn btn-brass">Calculate your price →</Link>
          </div>
        </div>
      </section>

      {/* ── WHEN TO CHOOSE ────────────────────────────────── */}
      <section className="section-sm">
        <div className="container-narrow">
          <p className="eyebrow page-eyebrow">When maintenance is right</p>
          <h2 className="display-md">The refresh without the reset.</h2>
          <div className="feature-list reveal">
            <div className="feature-item">
              <h3 className="feature-title">Your home is well-maintained</h3>
              <p className="feature-text">
                You keep things clean between cleanings. You&rsquo;re not deep-cleaning
                from neglect. You just want professional hands to cover what you can&rsquo;t.
              </p>
            </div>
            <div className="feature-item">
              <h3 className="feature-title">Annual refresh</h3>
              <p className="feature-text">
                Once a year (or once a season) your home gets a professional deep run.
                Baseboards, windows, inside cabinets. Then you maintain it yourself.
              </p>
            </div>
            <div className="feature-item">
              <h3 className="feature-title">Spring cleaning or seasonal reset</h3>
              <p className="feature-text">
                Spring arrived, or fall is coming. You want your home professionally
                reset before the season changes.
              </p>
            </div>
            <div className="feature-item">
              <h3 className="feature-title">Before guests arrive</h3>
              <p className="feature-text">
                Family is coming. You want your home pristine. Maintenance deep clean
                gets it ready without you doing the work.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── WHAT'S INCLUDED ───────────────────────────────── */}
      <section className="section-sm bg-bone-warm">
        <div className="container-narrow">
          <h2 className="display-md">What we clean on this visit</h2>
          <div className="checklist-grid reveal">
            <div className="checklist-col">
              <h3 className="checklist-heading">Kitchen</h3>
              <ul className="checklist">
                <li>Countertops and backsplash</li>
                <li>Sink and faucet</li>
                <li>Appliances (exterior)</li>
                <li>Cabinet fronts and handles</li>
                <li>Baseboards</li>
                <li>Light fixtures</li>
              </ul>
            </div>
            <div className="checklist-col">
              <h3 className="checklist-heading">Bathrooms</h3>
              <ul className="checklist">
                <li>Toilets and sinks</li>
                <li>Mirrors and fixtures</li>
                <li>Shower/tub and doors</li>
                <li>Floors and grout</li>
                <li>Ventilation fans</li>
              </ul>
            </div>
            <div className="checklist-col">
              <h3 className="checklist-heading">Living Spaces</h3>
              <ul className="checklist">
                <li>Vacuuming and mopping</li>
                <li>Dusting all surfaces</li>
                <li>Light switches and doors</li>
                <li>Interior windows</li>
                <li>Baseboards and trim</li>
                <li>Stair cleaning</li>
              </ul>
            </div>
            <div className="checklist-col">
              <h3 className="checklist-heading">Bedrooms</h3>
              <ul className="checklist">
                <li>Floors vacuumed</li>
                <li>Dusting all surfaces</li>
                <li>Baseboards</li>
                <li>Windows and sills</li>
                <li>Light switches</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── WHAT'S NOT INCLUDED ────────────────────────────── */}
      <section className="section-sm">
        <div className="container-narrow">
          <h2 className="display-md">How maintenance differs from a full deep clean.</h2>
          <p className="lede chapter-lede">
            Maintenance cleans what&rsquo;s <em>active and visible</em>. A full deep clean
            includes <em>behind-the-scenes areas</em>: inside cabinets, behind appliances,
            deep grout work, inside baseboards, and spaces that take extra hours.
          </p>
          <p className="lede chapter-lede chapter-lede-mt">
            If your home has been neglected or you&rsquo;re coming off a long period without
            professional cleaning, choose a <Link to="/deep" className="link-inline">Full Deep Clean</Link> instead.
          </p>
        </div>
      </section>

      {/* ── WHY NLC ────────────────────────────────────────── */}
      <section className="section-sm bg-bone-warm">
        <div className="container-narrow">
          <p className="eyebrow page-eyebrow">Why {branding.name}</p>
          <h2 className="display-md">Professionals who <em className="italic-display">respect your time.</em></h2>
          <div className="value-props reveal">
            <div className="value-prop">
              <h3>W-2 Employees Only</h3>
              <p>Your crew is employed by us. Benefits, training, accountability — all real.</p>
            </div>
            <div className="value-prop">
              <h3>Insured &amp; Background Checked</h3>
              <p>Every technician vetted. Insurance covers your home. You have recourse.</p>
            </div>
            <div className="value-prop">
              <h3>Per-Square-Foot Pricing</h3>
              <p>No surprises. No hidden fees. The price is clear upfront.</p>
            </div>
            <div className="value-prop">
              <h3>Focused Efficiency</h3>
              <p>We know exactly what maintenance entails. You get professional work without extra cost.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ────────────────────────────────────────────── */}
      <RelatedPages path="/maintenance" />

      <section className="cta-final">
        <div className="container-narrow cta-inner">
          <p className="cta-eyebrow">Ready for a refresh?</p>
          <h2>Book your maintenance deep clean</h2>
          <p className="cta-lede">See your price for your home. No obligation. Takes 60 seconds.</p>
          <Link to="/quote" className="btn btn-brass btn-lg">Get your price →</Link>
        </div>
      </section>
    </main>
  );
}
